import createClient, { type Middleware } from "openapi-fetch";

import { getCsrfToken } from "./csrf";
import { clearSoftSession } from "../lib/offline/offlineAuth";
import { isPublicAuthPath } from "../lib/publicAuthPath";
import type { paths } from "./schema";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const csrfAndSession: Middleware = {
  async onRequest({ request }) {
    const headers = new Headers(request.headers);
    if (!SAFE_METHODS.has(request.method.toUpperCase())) {
      const token = getCsrfToken();
      if (token) {
        headers.set("X-CSRF-Token", token);
      }
    }
    return new Request(request, {
      headers,
      credentials: "same-origin",
    });
  },
  async onResponse({ response, request }) {
    if (response.status === 401) {
      const path = new URL(request.url, window.location.origin).pathname;
      if (path.startsWith("/api/auth/")) {
        return response;
      }
      if (isPublicAuthPath(window.location.pathname)) {
        return response;
      }
      clearSoftSession();
      window.location.replace("/app/login");
    }
    return response;
  },
};

const client = createClient<paths>({
  baseUrl: "",
  credentials: "same-origin",
});

client.use(csrfAndSession);

type PathsWith<M extends string> = {
  [P in keyof paths]: M extends keyof paths[P] ? P : never;
}[keyof paths];

/** openapi-fetch składa FetchResponse do `never`, gdy 2xx jest puste albo brak 4xx. */
export type ApiResult = {
  data?: unknown;
  error?: unknown;
  response: Response;
};

type Call<M extends string> = (url: PathsWith<M>, init?: object) => Promise<ApiResult>;

export const api = {
  GET: client.GET as unknown as Call<"get">,
  PUT: client.PUT as unknown as Call<"put">,
  POST: client.POST as unknown as Call<"post">,
  PATCH: client.PATCH as unknown as Call<"patch">,
  DELETE: client.DELETE as unknown as Call<"delete">,
};
